import AuthContext from "@/context/auth";
import useAPI from "@/libs/hooks/api";
import { Badge, Box, Center, Flex, Image, Modal, ModalBody, ModalContent, ModalOverlay, Spinner, Text, useDisclosure, useToast, VStack } from "@chakra-ui/react";
import { useCallback, useContext, useEffect, useState } from "react";
import FAIcon from "./FAIcon";
import GameIframe from "./Game/GameIframe";

const ProfileGamesList = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [launchingGame, setLaunchingGame] = useState(false);
  const [launchingGameId, setLaunchingGameId] = useState(null);
  const [currentGame, setCurrentGame] = useState(null);
  const [currentGameUrl, setCurrentGameUrl] = useState(null);
  const { apiRequest } = useAPI();
  const toast = useToast();
  const { user } = useContext(AuthContext);
  
  const { isOpen: gameModalOpen, onOpen: openGameModal, onClose: closeGameModal } = useDisclosure();

  // Get thumbnail URL helper
  const getThumbnailUrl = (game) => {
    if (game.thumbnail) {
      const path = game.thumbnail.startsWith("/") ? game.thumbnail.slice(1) : game.thumbnail;
      return `https://assets.nightco.io/${path}`;
    }
    return null;
  };

  // Fetch available games
  useEffect(() => {
    const fetchGames = async () => {
      try {
        const response = await apiRequest("/jff_games", {
          method: "GET",
        });
        
        if (response?.data?.games) {
          // Limit to first 6 games for profile display
          setGames(response.data.games.slice(0, 6));
        } else {
          throw new Error("Failed to fetch games");
        }
      } catch (error) {
        console.error("Error fetching games:", error);
        // Don't show error toast for profile page, just fail silently
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, [apiRequest]);

  // Launch game
  const handleLaunchGame = useCallback(async (game) => {
    // Prevent double-clicks
    if (launchingGame) return;
    
    setLaunchingGame(true);
    setLaunchingGameId(game.id);
    
    try {
      const response = await apiRequest(`/jff_games/${game.id}`, {
        method: "POST",
        body: JSON.stringify({
          language: "zh-TW", // Default to Traditional Chinese
        }),
      });

      if (response?.data?.gameUrl) {
        setCurrentGame(game);
        setCurrentGameUrl(response.data.gameUrl);
        openGameModal();
      } else {
        throw new Error(response?.details || "Failed to launch game");
      }
    } catch (error) {
      console.error("Error launching game:", error);
      toast({
        title: "Launch Failed",
        description: typeof error === 'string' ? error : "Failed to launch game. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLaunchingGame(false);
      setLaunchingGameId(null);
    }
  }, [apiRequest, toast, openGameModal, launchingGame]);

  const handleCloseGame = useCallback(() => {
    setCurrentGame(null);
    setCurrentGameUrl(null);
    closeGameModal();
  }, [closeGameModal]);

  if (loading) {
    return (
      <Box px={4} py={2}>
        <Text color="white" fontSize="sm" textAlign="center">
          Loading games...
        </Text>
      </Box>
    );
  }

  if (games.length === 0) {
    return null; // Don't show anything if no games
  }

  return (
    <Box px={4} py={2}>
      <Flex align="center" mb={3} gap={2}>
        <FAIcon type="gamepad" fontSize="lg" color="white" /> 
        <Box w={1} h={2}  />
        <Text color="white" fontSize="lg" fontWeight="bold">
          小遊戲區
        </Text>
      </Flex>
      
      <Flex gap={3} overflowX="auto" pb={2} sx={{
        "::-webkit-scrollbar": {
          height: "4px",
        },
        "::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "::-webkit-scrollbar-thumb": {
          background: "whiteAlpha.300",
          borderRadius: "2px",
        },
        "::-webkit-scrollbar-thumb:hover": {
          background: "whiteAlpha.500",
        },
      }}>
        {games.map((game) => {
          const isLaunching = launchingGameId === game.id;
          return (
            <Box
              key={game.id}
              minW="120px"
              maxW="120px"
              cursor={isLaunching ? "not-allowed" : "pointer"}
              transition="all 0.2s"
              _hover={!isLaunching ? {
                transform: "translateY(-2px)",
                shadow: "lg",
              } : {}}
              onClick={() => !isLaunching && handleLaunchGame(game)}
              bg="blackAlpha.400"
              borderRadius="lg"
              p={2}
              border="1px solid"
              borderColor="whiteAlpha.200"
              backdropFilter="blur(10px)"
              opacity={isLaunching ? 0.7 : 1}
              position="relative"
            >
            <VStack spacing={2} align="stretch">
              {/* Game Image */}
              <Box
                position="relative"
                bg="gray.600"
                borderRadius="md"
                overflow="hidden"
                aspectRatio={1/1}
              >
                {getThumbnailUrl(game) ? (
                  <Image
                    src={getThumbnailUrl(game)}
                    alt={game.name_zh || game.name}
                    w="100%"
                    h="100%"
                    objectFit="contain"
                    fallback={
                      <Center h="100%" bg="gray.500">
                        <Text fontSize="lg">🎮</Text>
                      </Center>
                    }
                  />
                ) : (
                  <Center h="100%" bg="gray.500">
                    <Text fontSize="lg">🎮</Text>
                  </Center>
                )}
                
                {/* Game Type Badge */}
                <Badge
                  position="absolute"
                  top={1}
                  right={1}
                  bg={
                    game.type === 'origin' ? 'blue.500' :
                    game.type === 'scratch' ? 'green.500' : 'purple.500'
                  }
                  color="white"
                  textTransform="capitalize"
                  fontSize="xs"
                  borderRadius="full"
                  px={1.5}
                  py={0.5}
                >
                  {game.type}
                </Badge>
              </Box>

              {/* Game Name */}
              <Text 
                fontWeight="bold" 
                fontSize="xs" 
                noOfLines={2} 
                color="white"
                textAlign="center"
              >
                {game.name_zh || game.name}
              </Text>
            </VStack>

            {/* Loading Overlay */}
            {isLaunching && (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg="blackAlpha.600"
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
                backdropFilter="blur(2px)"
              >
                <VStack spacing={2}>
                  <Spinner size="md" color="white" />
                  <Text fontSize="xs" color="white" textAlign="center">
                    啟動中...
                  </Text>
                </VStack>
              </Box>
            )}
          </Box>
          );
        })}
      </Flex>

      {/* Game Modal */}
      <Modal isOpen={gameModalOpen} onClose={handleCloseGame} size="full" isCentered>
        <ModalOverlay bg="rgba(0,0,0,0.8)" />
        <ModalContent
          bg="transparent"
          boxShadow="none"
          border="none"
          maxW="90vw"
          maxH="90vh"
        >
          <ModalBody p={0}>
            {currentGame && currentGameUrl && (
              <GameIframe
                game={currentGame}
                gameUrl={currentGameUrl}
                onClose={handleCloseGame}
                height="90vh"
                showHeader={true}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProfileGamesList;
